import { Component, computed, effect, input, signal } from '@angular/core';
import {
  BaseFormControl,
  BaseSignalInput,
  SignalDropdown, SignalRadio,
  SignalRadioGroup,
  SignalRadioOption,
} from '@vallift/ngl';

export type SingleChoiceGridType = 'button' | 'radio' | 'dropdown';

export interface Choice {
  value: string;
  label: string;
}

export interface SingleChoiceGridOptions {
  id?: string;
  uniqueKey?: string;
  display: string;
  selectedChoiceId: string | null;
}

export interface SingleChoiceGridItem {
  id: string;
  uniqueKey: string;
  display: string;
}

/** Prefer selectedChoiceId; fall back to legacy selectedChoice. */
function readSelectedChoiceId(
  row: { selectedChoiceId?: string | null; selectedChoice?: string | null } | undefined,
): string | null {
  if (!row) {
    return null;
  }
  if (typeof row.selectedChoiceId === 'string' && row.selectedChoiceId.trim()) {
    return row.selectedChoiceId.trim();
  }
  if (typeof row.selectedChoice === 'string' && row.selectedChoice.trim()) {
    return row.selectedChoice.trim();
  }
  return null;
}


@Component({
  selector: 'app-single-choice-grid',
  imports: [BaseSignalInput, SignalDropdown, SignalRadio],
  templateUrl: './single-choice-grid.html',
  styleUrl: './single-choice-grid.css',
})
export class SingleChoiceGrid extends BaseFormControl<SingleChoiceGridOptions[]> {
  readonly label = input('');
  readonly helpText = input('');
  readonly items = input.required<(string | SingleChoiceGridItem)[]>();
  readonly options = input.required<Choice[]>();
  readonly responseType = input<SingleChoiceGridType>('button');
  readonly allowAdditionalRows = input(false);
  readonly addRowButtonLabel = input('+ Add another item');

  readonly locallyTouched = signal(false);
  readonly previewMode = input<'desktop' | 'mobile'>('desktop');

  readonly effectiveType = computed<SingleChoiceGridType>(() => {
    if (this.options().length > 3) {
      return 'dropdown';
    }

    return this.responseType();
  });

  readonly radioOptions = computed<readonly SignalRadioOption<string>[]>(() =>
    this.options().map((option) => ({
      value: option.value,
      label: option.label,
    })),
  );

  readonly rows = computed(() => {
    const value = this.value();
    const current: SingleChoiceGridOptions[] = Array.isArray(value) ? value : [];
    return this.syncRowsWithItems(this.items(), current);
  });

  readonly seedItemCount = computed(() => this.items().length);

  readonly localError = computed(() => {
    if (!this.required()) {
      return null;
    }

    const rows = this.rows();
    if (rows.length === 0) {
      return 'Please complete all items.';
    }

    const seedCount = this.seedItemCount();
    const incomplete = rows.some((row, index) => {
      if (!row.selectedChoiceId) {
        return true;
      }
      if (index >= seedCount && row.display.trim() === '') {
        return true;
      }
      return false;
    });

    return incomplete ? 'Please complete all items.' : null;
  });

  readonly displayMessage = computed(() => {
    if (this.disabled()) {
      return null;
    }

    if (this.showError()) {
      return this.errorMessage() ?? this.localError();
    }

    if (this.locallyTouched() || this.touched()) {
      return this.localError();
    }

    return null;
  });

  readonly displayError = computed(() => this.displayMessage() != null);
  constructor() {
    super();

    effect(() => {
      const items = this.items();
      const value = this.value();
      const current: SingleChoiceGridOptions[] = Array.isArray(value) ? value : [];
      const synced = this.syncRowsWithItems(items, current);
      if (!this.areRowsEqual(synced, current)) {
        this.value.set(synced);
      }
    });
  }

  selectOption(rowIndex: number, optionValue: unknown): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    this.updateRow(rowIndex, { selectedChoiceId: this.normalizeOptionValue(optionValue) });
    this.markLocallyTouched();
  }

  isSelected(rowIndex: number, optionValue: string): boolean {
    return this.rows()[rowIndex]?.selectedChoiceId === optionValue;
  }

  private normalizeOptionValue(optionValue: unknown): string | null {
    if (optionValue == null || optionValue === '') {
      return null;
    }

    if (typeof optionValue === 'string') {
      return optionValue;
    }

    if (
      typeof optionValue === 'object' &&
      optionValue !== null &&
      'value' in optionValue &&
      typeof (optionValue as Choice).value === 'string'
    ) {
      return (optionValue as Choice).value;
    }

    return String(optionValue);
  }

  updateDisplay(rowIndex: number, display: string): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    this.updateRow(rowIndex, { display });
    this.markLocallyTouched();
  }

  addRow(): void {
    if (this.disabled() || this.readonly() || !this.allowAdditionalRows()) {
      return;
    }

    this.value.set([
      ...this.rows(),
      {
        display: '',
        selectedChoiceId: null,
      },
    ]);

    this.markLocallyTouched();
  }

  removeRow(rowIndex: number): void {
    if (this.disabled() || this.readonly() || !this.canRemoveRow(rowIndex)) {
      return;
    }

    this.value.set(this.rows().filter((_, index) => index !== rowIndex));
    this.markLocallyTouched();
  }

  canRemoveRow(rowIndex: number): boolean {
    return rowIndex >= this.seedItemCount();
  }

  isUserAddedRowIndex(rowIndex: number): boolean {
    return rowIndex >= this.seedItemCount();
  }

  markLocallyTouched(): void {
    this.locallyTouched.set(true);
    this.onBlur();
  }

  private updateRow(rowIndex: number, updates: Partial<SingleChoiceGridOptions>): void {
    this.value.set(
      this.rows().map((row, index) =>
        index === rowIndex ? { ...row, ...updates } : row,
      ),
    );
  }
  private syncRowsWithItems(
    items: (string | SingleChoiceGridItem)[],
    current: SingleChoiceGridOptions[],
  ): SingleChoiceGridOptions[] {
    const seeded = items.map((item, index) => {
      const isCollectionItem = typeof item !== 'string';

      const display = isCollectionItem ? item.display : item;
      const itemId = isCollectionItem ? item.id : undefined;
      const itemUniqueKey = isCollectionItem ? item.uniqueKey || item.id : undefined;

      const existing =
        current.find((row) => itemId != null && row.id === itemId) ??
        current.find((row) => itemUniqueKey != null && row.uniqueKey === itemUniqueKey) ??
        (current[index]?.display === display ? current[index] : undefined) ??
        current.find((row) => row.display === display);

      return {
        ...((itemId ?? existing?.id) ? { id: itemId ?? existing?.id } : {}),
        ...((itemUniqueKey ?? existing?.uniqueKey)
          ? { uniqueKey: itemUniqueKey ?? existing?.uniqueKey }
          : {}),
        display,
        selectedChoiceId: readSelectedChoiceId(existing),
      };
    });

    return [...seeded, ...current.slice(items.length)];
  }

  private areRowsEqual(a: SingleChoiceGridOptions[], b: SingleChoiceGridOptions[]): boolean {
    if (a.length !== b.length) {
      return false;
    }

    return a.every(
      (row, index) =>
        row.id === b[index]?.id &&
        row.uniqueKey === b[index]?.uniqueKey &&
        row.display === b[index]?.display &&
        row.selectedChoiceId === b[index]?.selectedChoiceId,
    );
  }

  onDropdownChange(rowIndex: number, event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectOption(rowIndex, select.value === '' ? null : select.value);
  }
}
